// services/courses.ts
//
// Courses V1, the backend's contract: ora-workspace/docs/courses/
// FRONTEND-INTEGRATION.md (Kevin, 2026-10-22), section 3.
//
// Every route is under /api/v5/courses with the usual `{ success, data }`
// envelope. Two refusals are part of the flow rather than failures: the
// feature gate (`not_found`, read as "no courses") and `STEP_INCOMPLETE` on
// `complete` (409, with `data.missing` saying what is left). The practice
// conversation's 409s (`PRACTICE_*`) are thrown as `CoursePracticeRefusal`.
//
// Until the routes exist, a dev build with `VITE_COURSES_MOCK=true` serves the
// same contract from the guide's own fixtures. The mock module is imported
// lazily behind `import.meta.env.DEV`, so a production bundle never carries it.

import axios from 'axios';
import axiosInstance from './axiosCreator';
import { isCoursesMockEnabled } from './coursesMockFlag';
import type {
  CourseAssessment,
  CourseDetail,
  CourseModuleDetail,
  CoursePracticeSession,
  CourseReviewLaunch,
  CourseStepCompleteResult,
  CourseStepMissing,
  CourseStepStartResult,
  CourseSummary,
} from '../types/course';

export const COURSES_ROUTE = '/api/v5/courses';

/** The 409 codes of the practice conversation routes. */
export type CoursePracticeRefusalCode =
  | 'PRACTICE_DAILY_LIMIT'
  | 'PRACTICE_FINISHED'
  | 'PRACTICE_MAX_TURNS'
  | 'PRACTICE_NOTHING_TO_RETRY'
  | 'PRACTICE_TOO_SHORT';

const PRACTICE_REFUSAL_CODES: readonly string[] = [
  'PRACTICE_DAILY_LIMIT',
  'PRACTICE_FINISHED',
  'PRACTICE_MAX_TURNS',
  'PRACTICE_NOTHING_TO_RETRY',
  'PRACTICE_TOO_SHORT',
];

/** A practice route refused (409): part of the flow, shown in plain words. */
export class CoursePracticeRefusal extends Error {
  constructor(readonly code: CoursePracticeRefusalCode) {
    super(code);
    this.name = 'CoursePracticeRefusal';
  }
}

export type CourseStepCompleteOutcome =
  | { completed: true; result: CourseStepCompleteResult }
  | { completed: false; message: string; missing: CourseStepMissing };

export interface CoursesApi {
  /** The student's courses. Empty when the feature is gated off for them. */
  listCourses: () => Promise<CourseSummary[]>;
  /** `courseId` is the course id or its slug. */
  getCourse: (courseId: string) => Promise<CourseDetail>;
  getCourseModule: (
    courseId: string,
    moduleId: string
  ) => Promise<CourseModuleDetail>;
  /** Idempotent. `restart` makes a new block / fresh `startedAt`. */
  startCourseStep: (
    courseId: string,
    stepId: string,
    options?: { restart?: boolean }
  ) => Promise<CourseStepStartResult>;
  /** Explicit, idempotent; the server checks the completion rule. */
  completeCourseStep: (
    courseId: string,
    stepId: string
  ) => Promise<CourseStepCompleteOutcome>;
  /** Ephemeral: writes no progress. */
  startCourseReview: (
    courseId: string,
    moduleId?: string
  ) => Promise<CourseReviewLaunch>;
  /**
   * ASSESSMENT steps: create the step's one attempt (flexible timing), or
   * return the existing one as it is. The attempt itself is taken through the
   * self-assessment block route.
   */
  startCourseAssessment: (
    courseId: string,
    stepId: string
  ) => Promise<CourseAssessment>;
  /**
   * CONVERSATION steps: a new session with the client's opening line, or the
   * ACTIVE one as it is. `restart` abandons the ACTIVE one and starts fresh.
   * Refuses with PRACTICE_DAILY_LIMIT.
   */
  startPracticeSession: (
    courseId: string,
    stepId: string,
    options?: { restart?: boolean }
  ) => Promise<CoursePracticeSession>;
  /**
   * The student's turn, coded, and the client's reply. At the session's
   * maximum the session comes back finished, with its feedback.
   */
  sendPracticeTurn: (
    courseId: string,
    stepId: string,
    sessionId: string,
    text: string
  ) => Promise<CoursePracticeSession>;
  /** Remove the latest student turn (one with a coach nudge) and its reply. */
  retryPracticeTurn: (
    courseId: string,
    stepId: string,
    sessionId: string
  ) => Promise<CoursePracticeSession>;
  /** Stats and feedback; completes the step at `minTurns` or more. */
  finishPracticeSession: (
    courseId: string,
    stepId: string,
    sessionId: string
  ) => Promise<CoursePracticeSession>;
  /**
   * A client turn's voice (a turn's `audioPath`), fetched with the session's
   * credentials, as an object URL the caller revokes.
   */
  practiceTurnAudioUrl: (
    audioPath: string,
    options?: { signal?: AbortSignal }
  ) => Promise<string>;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readData = <T>(body: unknown): T => {
  const data = asRecord(body)?.data;
  if (data === undefined) {
    throw new Error('Courses response carried no data');
  }
  return data as T;
};

/** The `{ status, code, body }` of an axios rejection, when it is one. */
const readRefusal = (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.response) return null;
  const body = asRecord(error.response.data);
  const code = typeof body?.code === 'string' ? body.code : null;
  return { status: error.response.status, code, body };
};

const coursePath = (courseId: string) =>
  `${COURSES_ROUTE}/${encodeURIComponent(courseId)}`;

const stepPath = (courseId: string, stepId: string) =>
  `${coursePath(courseId)}/steps/${encodeURIComponent(stepId)}`;

const practicePath = (courseId: string, stepId: string, sessionId?: string) =>
  `${stepPath(courseId, stepId)}/practice/sessions${
    sessionId ? `/${encodeURIComponent(sessionId)}` : ''
  }`;

/** A practice route's session, or its 409 as a `CoursePracticeRefusal`. */
const practiceRequest = async (
  request: () => Promise<{ data: unknown }>
): Promise<CoursePracticeSession> => {
  try {
    const response = await request();
    return readData<{ session: CoursePracticeSession }>(response.data).session;
  } catch (error) {
    const refusal = readRefusal(error);
    if (
      refusal?.status === 409 &&
      refusal.code &&
      PRACTICE_REFUSAL_CODES.includes(refusal.code)
    ) {
      throw new CoursePracticeRefusal(
        refusal.code as CoursePracticeRefusalCode
      );
    }
    throw error;
  }
};

const realCoursesApi: CoursesApi = {
  listCourses: async () => {
    try {
      const response = await axiosInstance.get(COURSES_ROUTE);
      return readData<{ courses: CourseSummary[] }>(response.data).courses;
    } catch (error) {
      // The feature gate answers a bare 404 (`{ "error": "not_found" }`, no
      // envelope): no courses for this student, rather than a failure to
      // show them. Any 404 here reads that way (COMPANION-FRONTEND-HANDOFF 1).
      if (readRefusal(error)?.status === 404) return [];
      throw error;
    }
  },

  getCourse: async (courseId) => {
    const response = await axiosInstance.get(coursePath(courseId));
    return readData<CourseDetail>(response.data);
  },

  getCourseModule: async (courseId, moduleId) => {
    const response = await axiosInstance.get(
      `${coursePath(courseId)}/modules/${encodeURIComponent(moduleId)}`
    );

    return readData<CourseModuleDetail>(response.data);
  },

  startCourseStep: async (courseId, stepId, options = {}) => {
    const response = await axiosInstance.post(
      `${stepPath(courseId, stepId)}/start`,
      options.restart ? { restart: true } : {}
    );

    return readData<CourseStepStartResult>(response.data);
  },

  completeCourseStep: async (courseId, stepId) => {
    try {
      const response = await axiosInstance.post(
        `${stepPath(courseId, stepId)}/complete`
      );

      return {
        completed: true,
        result: readData<CourseStepCompleteResult>(response.data),
      };
    } catch (error) {
      const refusal = readRefusal(error);
      if (refusal?.status === 409 && refusal.code === 'STEP_INCOMPLETE') {
        const missing = asRecord(asRecord(refusal.body?.data)?.missing) ?? {};
        return {
          completed: false,
          message:
            typeof refusal.body?.error === 'string'
              ? refusal.body.error
              : 'This step is not finished yet.',
          missing: missing as CourseStepMissing,
        };
      }
      throw error;
    }
  },

  startCourseReview: async (courseId, moduleId) => {
    const response = await axiosInstance.post(
      `${coursePath(courseId)}/review/start`,
      moduleId ? { moduleId } : {}
    );

    return readData<CourseReviewLaunch>(response.data);
  },

  startCourseAssessment: async (courseId, stepId) => {
    const response = await axiosInstance.post(
      `${stepPath(courseId, stepId)}/assessment/start`,
      {}
    );

    return readData<{ assessment: CourseAssessment }>(response.data).assessment;
  },

  startPracticeSession: (courseId, stepId, options = {}) =>
    practiceRequest(() =>
      axiosInstance.post(
        practicePath(courseId, stepId),
        options.restart ? { restart: true } : {}
      )
    ),

  sendPracticeTurn: (courseId, stepId, sessionId, text) =>
    practiceRequest(() =>
      axiosInstance.post(`${practicePath(courseId, stepId, sessionId)}/turns`, {
        text,
      })
    ),

  retryPracticeTurn: (courseId, stepId, sessionId) =>
    practiceRequest(() =>
      axiosInstance.post(`${practicePath(courseId, stepId, sessionId)}/retry`)
    ),

  finishPracticeSession: (courseId, stepId, sessionId) =>
    practiceRequest(() =>
      axiosInstance.post(`${practicePath(courseId, stepId, sessionId)}/finish`)
    ),

  practiceTurnAudioUrl: async (audioPath, options = {}) => {
    // The path is the API's own (relative to the backend), so axios sends
    // the session cookie with it, as every other course route.
    const response = await axiosInstance.get<Blob>(audioPath, {
      responseType: 'blob',
      signal: options.signal,
    });

    return URL.createObjectURL(response.data);
  },
};

/** The voice is off on the server (503 `tts_unavailable`), not just failed. */
export const isPracticeAudioUnavailable = (error: unknown): boolean =>
  readRefusal(error)?.status === 503;

// The switch lives in its own module (the instance hooks read it); the
// course screens and their tests keep reading it from here.
export { isCoursesMockEnabled };

let resolvedApi: Promise<CoursesApi> | null = null;

const coursesApi = (): Promise<CoursesApi> => {
  if (!resolvedApi) {
    resolvedApi = isCoursesMockEnabled()
      ? import('./courses.mock').then((module) => module.coursesMockApi)
      : Promise.resolve(realCoursesApi);
  }
  return resolvedApi;
};

export const listCourses: CoursesApi['listCourses'] = async () =>
  (await coursesApi()).listCourses();

export const getCourse: CoursesApi['getCourse'] = async (courseId) =>
  (await coursesApi()).getCourse(courseId);

export const getCourseModule: CoursesApi['getCourseModule'] = async (
  courseId,
  moduleId
) => (await coursesApi()).getCourseModule(courseId, moduleId);

export const startCourseStep: CoursesApi['startCourseStep'] = async (
  courseId,
  stepId,
  options
) => (await coursesApi()).startCourseStep(courseId, stepId, options);

export const completeCourseStep: CoursesApi['completeCourseStep'] = async (
  courseId,
  stepId
) => (await coursesApi()).completeCourseStep(courseId, stepId);

export const startCourseReview: CoursesApi['startCourseReview'] = async (
  courseId,
  moduleId
) => (await coursesApi()).startCourseReview(courseId, moduleId);

export const startCourseAssessment: CoursesApi['startCourseAssessment'] =
  async (courseId, stepId) =>
    (await coursesApi()).startCourseAssessment(courseId, stepId);

export const startPracticeSession: CoursesApi['startPracticeSession'] = async (
  courseId,
  stepId,
  options
) => (await coursesApi()).startPracticeSession(courseId, stepId, options);

export const sendPracticeTurn: CoursesApi['sendPracticeTurn'] = async (
  courseId,
  stepId,
  sessionId,
  text
) => (await coursesApi()).sendPracticeTurn(courseId, stepId, sessionId, text);

export const retryPracticeTurn: CoursesApi['retryPracticeTurn'] = async (
  courseId,
  stepId,
  sessionId
) => (await coursesApi()).retryPracticeTurn(courseId, stepId, sessionId);

export const finishPracticeSession: CoursesApi['finishPracticeSession'] =
  async (courseId, stepId, sessionId) =>
    (await coursesApi()).finishPracticeSession(courseId, stepId, sessionId);

export const practiceTurnAudioUrl: CoursesApi['practiceTurnAudioUrl'] = async (
  audioPath,
  options
) => (await coursesApi()).practiceTurnAudioUrl(audioPath, options);

/** Test seam: swap the api behind every export. */
export const __setCoursesApiForTests = (api: CoursesApi | null) => {
  resolvedApi = api ? Promise.resolve(api) : null;
};
